import { getFirebaseApp, getAuthData } from '../katapult-job/KatapultJobUtilities.js';

/** @import { ClientOrAdminFirebase } from '../types/ClientOrAdminFirebase.js' */

/** @import { OnSnapshotCallback, UserToolset, UserToolsets } from './KatapultUserTypes.d.ts' */

/**
 * @typedef {Object} KatapultUserOptions Options for the user object.
 * @property {ClientOrAdminFirebase} [firebaseApp] - The Firebase app to use for backend operations.
 * @property {boolean} [autoCache] - Automatically cache data on "gets", cleared by {@link KatapultUser.destroy}.
 */

export class KatapultUser {
  /**
   * The company the user's own record is filed under.
   * @type {string}
   */
  #rootCompany;

  /**
   * The uid of the user.
   * @type {string}
   */
  #id;

  /**
   * Auto-cache user data.
   * @type {boolean}
   */
  #autoCache = false;

  /**
   * Record of snapshot listener lists by path.
   * @type {Record<string, OnSnapshotCallback[]>}
   */
  #listenersByPath = {};

  /**
   * Record of caching listeners by path.
   * @type {Record<string, OnSnapshotCallback>}
   */
  #cachingListenersByPath = {};

  /**
   * A Katapult Pro user data object.
   * @param {string} rootCompany - The company the user's own record is filed under.
   * @param {string} uid - The uid of the user.
   * @param {KatapultUserOptions} options - Options for the data object.
   */
  constructor(rootCompany, uid, options = {}) {
    // Require both segments of the user's path
    if (!rootCompany) throw new Error("A user's root company is required to reach their own record.");
    if (!uid) throw new Error('A uid is required to reach a user record.');

    this.#rootCompany = rootCompany;
    this.#id = uid;
    this.firebase = options?.firebaseApp ?? getFirebaseApp();
    this.#autoCache = options.autoCache ?? false;
    this.database = this.firebase.database();
    this.userPath = `users/${rootCompany}/${uid}`;
    this.reference = this.database.ref(this.userPath);
  }

  /**
   * The uid of the user.
   * @type {string}
   */
  get id() {
    return this.#id;
  }

  /**
   * The company the user's own record is filed under.
   * @type {string}
   */
  get rootCompany() {
    return this.#rootCompany;
  }

  /**
   * Whether auto-caching listeners will be set up on data "gets".
   * @type {boolean}
   */
  get autoCache() {
    return this.#autoCache;
  }

  /**
   * Get a new Katapult User object from a root company and a uid.
   * @param {string} rootCompany - The company the user's own record is filed under.
   * @param {string} uid - The uid of the user.
   * @param {KatapultUserOptions} [options] - Options for the user object.
   * @returns {KatapultUser} The user object.
   */
  static fromId(rootCompany, uid, options) {
    return new KatapultUser(rootCompany, uid, options);
  }

  /**
   * Get a new Katapult User object for the user making Firebase requests.
   * @param {KatapultUserOptions} [options] - Options for the user object.
   * @returns {Promise<KatapultUser>} The user object for the authenticated user.
   */
  static async fromCurrentUser(options) {
    // Take both path segments from the auth token
    const firebase = options?.firebaseApp ?? getFirebaseApp();
    const authData = await getAuthData(firebase);
    if (!authData) throw new Error('A signed-in user is required to build a user object from the current user.');

    return new KatapultUser(authData.root_company, authData.uid, { ...options, firebaseApp: firebase });
  }

  /**
   * Destroy the user object, removing all listeners.
   * @returns {void}
   */
  destroy() {
    // Remove all listeners for each path.
    const listenersByPathEntries = Object.entries(this.#listenersByPath);
    for (const [path, listeners] of listenersByPathEntries) {
      for (const listener of listeners) {
        this.reference.child(path).off('value', listener);
      }
    }

    // Remove all caching listeners.
    const cachingListenersByPathEntries = Object.entries(this.#cachingListenersByPath);
    for (const [path, listener] of cachingListenersByPathEntries) {
      this.reference.child(path).off('value', listener);
    }

    // Clear the listener paths.
    this.#listenersByPath = {};
    this.#cachingListenersByPath = {};
  }

  /**
   * Get the toolsets the user keeps for one model.
   * @param {string} modelId - The model space id the toolsets are scoped to.
   * @returns {Promise<UserToolsets|null>} The user's toolsets for that model, or null when they keep none.
   */
  async getUserToolsets(modelId) {
    const userToolsetsPath = this.#buildUserToolsetsPath(modelId);
    return await this.#readDataOnce(userToolsetsPath);
  }

  /**
   * Listen for changes to the toolsets the user keeps for one model.
   * @param {string} modelId - The model space id the toolsets are scoped to.
   * @param {OnSnapshotCallback<UserToolsets>} callback - The callback function to be called when the user's toolsets change.
   * @returns {() => void} A function to remove the listener.
   */
  onUserToolsetsSnapshot(modelId, callback) {
    const userToolsetsPath = this.#buildUserToolsetsPath(modelId);
    return this.#setUpListener(userToolsetsPath, callback);
  }

  /**
   * Set one of the toolsets the user keeps for one model.
   * @param {string} modelId - The model space id the toolset is scoped to.
   * @param {string} toolsetName - The name the toolset is stored under.
   * @param {UserToolset|null} userToolset - The toolset to store, or null to clear it.
   * @returns {Promise<void>} A promise that resolves once the toolset has been stored.
   */
  async setUserToolset(modelId, toolsetName, userToolset) {
    if (!toolsetName) throw new Error('A toolset name is required to store a user toolset.');

    const userToolsetsPath = this.#buildUserToolsetsPath(modelId);
    await this.#setDataOnce(`${userToolsetsPath}/${toolsetName}`, userToolset);
  }

  /**
   * Set whether Firebase console links open with the user's default Google account instead of their second one.
   * @param {boolean} useDefaultGoogleAccount - Whether to open Firebase console links with the default Google account.
   * @returns {Promise<void>} A promise that resolves once the preference has been stored.
   */
  async setFirebaseConsoleUseDefaultGoogleAccount(useDefaultGoogleAccount) {
    // Clear the preference rather than storing false, since off is the default
    await this.#setDataOnce('preferences/firebase_console_use_default_google_account', useDefaultGoogleAccount ? true : null);
  }

  /**
   * Build the path to the user's toolsets for one model.
   * @param {string} modelId - The model space id the toolsets are scoped to.
   * @returns {string} The path relative to the user's own record.
   */
  #buildUserToolsetsPath(modelId) {
    if (!modelId) throw new Error('A model id is required to reach a user toolset.');

    return `preferences/user_toolsets/${modelId}`;
  }

  /**
   * Set up a no-op listener that caches user data in memory.
   * @param {string} path - The path to listen to.
   */
  #setUpCachingListener(path) {
    // Do nothing if the listener has already been set up.
    if (this.#cachingListenersByPath[path]) return;

    // Do nothing if there is a caching listener at a shallower path.
    const pathParts = path.split('/').slice(0, -1);
    for (const index in pathParts) {
      const parentPath = pathParts.slice(0, Number(index) + 1).join('/');
      if (this.#cachingListenersByPath[parentPath]) return;
    }

    // Set up the no-op listener.
    const childRef = this.reference.child(path);
    const listener = childRef.on('value', () => {});
    this.#cachingListenersByPath[path] = listener;

    // If there are caching listeners on a child path of this path, remove them.
    const childPathsWithListeners = Object.keys(this.#cachingListenersByPath).filter((childPath) => childPath.startsWith(`${path}/`));
    for (const childPath of childPathsWithListeners) {
      this.reference.child(childPath).off('value', this.#cachingListenersByPath[childPath]);
      delete this.#cachingListenersByPath[childPath];
    }
  }

  /**
   * Sets up a listener for a child path.
   * @param {string} childPath - The child path to listen to.
   * @param {OnSnapshotCallback} callback - The callback function to be called when the child path changes.
   * @returns {() => void} A function to remove the listener.
   */
  #setUpListener(childPath, callback) {
    // If desired, cache user data for this path.
    if (this.#autoCache) this.#setUpCachingListener(childPath);

    // Set up the specified listener, which the worker adapter cannot report denials through
    const childRef = this.reference.child(childPath);
    childRef.on('value', callback);

    // Keep a record of the listener, and return a function to remove it.
    this.#listenersByPath[childPath] ??= [];
    this.#listenersByPath[childPath].push(callback);
    const listenerUnsubscriber = () => {
      childRef.off('value', callback);
      this.#listenersByPath[childPath] = this.#listenersByPath[childPath].filter((listener) => listener !== callback);
    };

    return listenerUnsubscriber;
  }

  /**
   * Reads data from a path once, using cached data when a listener supplies it.
   * @param {string} path - The path to read from, relative to the user's own record.
   * @returns {Promise<any>} The data at the path.
   */
  async #readDataOnce(path) {
    // If desired, cache user data for this path.
    if (this.#autoCache) this.#setUpCachingListener(path);

    // Read the data from the path.
    const snapshot = await this.reference
      .child(path)
      .once('value')
      .catch((error) => {
        const { message } = error;
        throw new Error(message);
      });
    return snapshot.val();
  }

  /**
   * Writes data to a path, replacing whatever it holds now.
   * @param {string} path - The path to write to, relative to the user's own record.
   * @param {any} value - The value to store at the path.
   * @returns {Promise<void>} A promise that resolves once the data has been stored.
   */
  async #setDataOnce(path, value) {
    await this.reference
      .child(path)
      .set(value)
      .catch((error) => {
        const { message } = error;
        throw new Error(message);
      });
  }
}
